import { MouseEvent, useCallback, useMemo } from 'react'

import { usePrintScale } from '@editor/hooks/usePrintScale'
import { CircleMeshProps } from '@editor/interfaces/canvas/circleMesh'
import { DrawingParameterCircle, DrawingType } from '@editor/interfaces/drawing'
import { CanvasConfigV2, CanvasEventsExtra } from '@editor/interfaces/editor'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { BlueprintEditorTools, DEFAULT_STYLES, Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import useDrawingStateCheck from '@editor/pages/projects/blueprint-editor/hooks/useDrawingStateCheck'
import usePointSelection from '@editor/pages/projects/blueprint-editor/hooks/usePointSelection'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { addDrawing, updateDrawingParameters } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { getDrawingsOfType } from '@editor/services/Drawing'
import { convertPrintDimension } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { selectCurrentAnchorPositionByTool } from '@editor/store/selectors'
import { setWorkingElementId } from '@editor/store/tool'
import { useSelector } from 'react-redux'
import { Matrix4, Matrix4Tuple, Vector3 } from 'three'

import { getLastCount, getNewOrder } from '../../../utils'
import { WORKING_ID } from '../constants'
import { setWorkingCirclePoint } from '../store'
import useUndo from './useUndo'

// Selector for current anchor position specific to Circle tool, for re-rendering optimization
const selectCurrentAnchorPosition = selectCurrentAnchorPositionByTool(BlueprintEditorTools.Circle)

const useMainCanvas = (): CanvasConfigV2 => {
  // Hooks
  const { projectionDirections, calculateOffsetMatrix } = useProjectionPlane()
  const { handleUndo } = useUndo()
  const { currentPrintScale } = usePrintScale()
  const { editingElementId, isSelectable } = useDrawingStateCheck()
  const { getIntersection } = usePointSelection()

  // Contexts
  const { arcballControls } = useBlueprintEditorContext()

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)

  const hiddenElementIds = useSelector((state: RootState) => state.toolUtil.core.hiddenElementIds)
  const workingElementId = useSelector((state: RootState) => state.toolUtil.core.workingElementId)
  const currentAnchorPosition = useSelector(selectCurrentAnchorPosition)

  const selectedBlueprint = useSelector((state: RootState) => state.blueprint.editor.selectedBlueprint)
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const mode = useSelector((state: RootState) => state.blueprint.editor.mode)
  const isAllowedToModify = useSelector((state: RootState) => state.blueprint.editor.isAllowedToModify)

  const drawings = useSelector((state: RootState) => state.blueprint.drawings.document.drawings)
  const renderSkipUuids = useSelector((state: RootState) => state.blueprint.drawings.renderSkipUuids)
  const defaultStyles = useSelector((state: RootState) => state.blueprint.drawings.defaultStyles)

  const workingCirclePoint = useSelector((state: RootState) => state.bpToolCircle.workingCirclePoint)

  // Vars
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.Circle, [selectedTool])
  const workingMatrix = useMemo(() => {
    if (!workingCirclePoint || !projectionDirections) return null

    const origin = new Vector3(...workingCirclePoint)

    const z = projectionDirections.normal.clone()
    const x = projectionDirections.left.clone()
    const y = projectionDirections.up.clone()

    const m = new Matrix4()
    m.makeBasis(x, y, z)
    m.setPosition(origin)

    return m
  }, [projectionDirections, workingCirclePoint])

  /**
   * Completes the circle drawing when the user releases the mouse button.
   */
  const onMouseUp = useCallback(
    (ev: MouseEvent<HTMLDivElement>, { isDragged }: CanvasEventsExtra) => {
      if (
        !isToolSelected ||
        !arcballControls.current ||
        isDragged ||
        !project ||
        !inspectionArea ||
        !selectedBlueprint?.sort_key ||
        editingElementId
      )
        return

      const point = getIntersection(ev)
      if (!point) return

      // If there is no working circle point, set it and return
      if (!workingCirclePoint) {
        dispatch(setWorkingCirclePoint(point))
        dispatch(setWorkingElementId(WORKING_ID))
        return
      }

      if (!workingMatrix) return

      // Calculate radius based on the distance from the working circle point to the current anchor position
      const radius = new Vector3(...workingCirclePoint).distanceTo(new Vector3(...point))

      dispatch(
        addDrawing({
          name: `円 ${
            getLastCount(
              drawings.map((p) => p.name),
              '円',
            ) + 1
          }`,
          drawing_type: DrawingType.Circle,
          drawing_parameters: {
            uuid: crypto.randomUUID(),
            center: workingCirclePoint,
            transformation: workingMatrix.toArray(),
            radius,
            order: getNewOrder(drawings),
            styles: defaultStyles,
          },
        }),
      )
      dispatch(setWorkingCirclePoint(undefined))
      dispatch(setWorkingElementId(undefined))
    },
    [
      getIntersection,
      dispatch,
      editingElementId,
      project,
      inspectionArea,
      selectedBlueprint,
      drawings,
      workingMatrix,
      workingCirclePoint,
      arcballControls,
      isToolSelected,
      defaultStyles,
    ],
  )

  return {
    objects: {
      circleAnchors: [
        // Current is off by settings, but we want to to show it when setting the center point
        ...useMemo(
          () =>
            isToolSelected
              ? [
                  {
                    id: 'current-anchor',
                    point: currentAnchorPosition || [0, 0, 0],
                    visible: !!currentAnchorPosition && !workingCirclePoint,
                  },
                ]
              : [],
          [currentAnchorPosition, isToolSelected, workingCirclePoint],
        ),
      ],
      circleMeshes: [
        // Completed circles
        ...useMemo(
          () =>
            getDrawingsOfType<DrawingParameterCircle>(
              drawings,
              DrawingType.Circle,
              renderSkipUuids,
            ).map<CircleMeshProps>(({ drawing_parameters: { uuid, transformation, radius, styles, order } }) => ({
              id: uuid,
              transformation,
              radius,
              isEditing: editingElementId === uuid,
              visible: !hiddenElementIds.includes(uuid) && mode !== Mode.Editor3D,
              color: styles?.fillColor || 'transparent',
              outline: {
                style: styles?.strokeStyle || DEFAULT_STYLES.strokeStyle,
                color: styles?.strokeColor,
                thickness: convertPrintDimension(styles?.strokeWidth || 1, currentPrintScale),
              },
              selectable: isSelectable(uuid) && isAllowedToModify,
              offsetTransformation: calculateOffsetMatrix((order ?? 1) + 1).toArray(), // Offset by order for layering
              onComplete: (updatedTransformation: Matrix4Tuple, rad: number) => {
                dispatch(
                  updateDrawingParameters({
                    uuid,
                    transformation: updatedTransformation,
                    radius: rad,
                  }),
                )
              },
            })),
          [
            drawings,
            renderSkipUuids,
            hiddenElementIds,
            editingElementId,
            mode,
            currentPrintScale,
            isAllowedToModify,
            isSelectable,
            calculateOffsetMatrix,
            dispatch,
          ],
        ),

        // Working circles
        ...useMemo(
          (): CircleMeshProps[] =>
            workingMatrix
              ? [
                  {
                    id: 'working-circle',
                    transformation: workingMatrix.toArray(),
                    radius: 0, // Radius will be calculated dynamically through currentAnchorPosition
                    isWorking: true,
                    color: defaultStyles?.fillColor || 'transparent',
                    outline: {
                      style: defaultStyles?.strokeStyle || DEFAULT_STYLES.strokeStyle,
                      color: defaultStyles?.strokeColor,
                      thickness: convertPrintDimension(defaultStyles?.strokeWidth || 1, currentPrintScale),
                    },
                  },
                ]
              : [],
          [workingMatrix, defaultStyles, currentPrintScale],
        ),
      ],
    },
    events: {
      onMouseUp,
      onUndo: useCallback(() => {
        if (!isToolSelected) return

        handleUndo()
      }, [isToolSelected, handleUndo]),
      onCancel: useCallback(() => {
        if (!isToolSelected) return

        if (workingElementId) {
          dispatch(setWorkingCirclePoint(undefined))
          dispatch(setWorkingElementId(undefined))
        }
      }, [isToolSelected, workingElementId, dispatch]),
    },
  }
}

export default useMainCanvas
